import React, { useState } from "react";
import { Calendar, Clock, User, MapPin, Phone, Mail, CreditCard, Accessibility, Users, AlertTriangle, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookRide = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    pickupAddress: "",
    dropoffAddress: "",
    tripDate: "",
    tripTime: "",
    transportationType: "",
    paymentMethod: "",
    specialRequests: "",
    // Emergency contacts
    emergencyContact1Name: "",
    emergencyContact1Phone: "",
    emergencyContact1Relation: "",
    emergencyContact2Name: "",
    emergencyContact2Phone: "",
    emergencyContact2Relation: "",
    // Medical information
    mobilityAids: "",
    medicalEquipment: "",
    assistanceLevel: "",
    // Trip preferences
    reminderPreference: "",
    returnTrip: false,
    returnTime: "",
    // Insurance information
    insuranceCompany: "",
    memberId: "",
    // Consent
    consentToContact: false,
    consentToShare: false
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/book-ride`, formData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Reset form
        setFormData({
          name: "",
          phone: "",
          email: "",
          pickupAddress: "",
          dropoffAddress: "",
          tripDate: "",
          tripTime: "",
          transportationType: "",
          paymentMethod: "",
          specialRequests: "",
          emergencyContact1Name: "",
          emergencyContact1Phone: "",
          emergencyContact1Relation: "",
          emergencyContact2Name: "",
          emergencyContact2Phone: "",
          emergencyContact2Relation: "",
          mobilityAids: "",
          medicalEquipment: "",
          assistanceLevel: "",
          reminderPreference: "",
          returnTrip: false,
          returnTime: "",
          insuranceCompany: "",
          memberId: "",
          consentToContact: false,
          consentToShare: false
        });
      }
    } catch (error) {
      console.error("Booking submission error:", error);
      const errorMessage = error.response?.data?.detail || 
                          "There was an error submitting your request. Please try again or call us directly at (269) 248-3514.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Book Your Ride</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Schedule your transportation with Quality Transport Services. We provide safe, 
            reliable medical transportation for all your needs.
          </p>
        </div>

        {/* Important Notice */}
        <Card className="mb-8 border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-start space-x-3">
              <Clock className="h-6 w-6 text-amber-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800 mb-2">Scheduling Notice</h3>
                <p className="text-amber-700">
                  We request 24 hours' notice for long-distance trips, but we are happy to work 
                  with last-minute requests whenever possible. For urgent needs, please call us 
                  directly at <span className="font-semibold">(269) 248-3514</span>.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Booking Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900">Transportation Request Form</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="flex items-center space-x-2">
                    <User className="h-4 w-4" />
                    <span>Full Name *</span>
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="Enter your full name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="flex items-center space-x-2">
                    <Phone className="h-4 w-4" />
                    <span>Phone Number *</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    placeholder="(269) 123-4567"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center space-x-2">
                  <Mail className="h-4 w-4" />
                  <span>Email Address *</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="your.email@example.com"
                  required
                />
              </div>

              {/* Trip Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="pickupAddress" className="flex items-center space-x-2">
                    <MapPin className="h-4 w-4" />
                    <span>Pickup Address *</span>
                  </Label>
                  <Textarea
                    id="pickupAddress"
                    value={formData.pickupAddress}
                    onChange={(e) => handleInputChange("pickupAddress", e.target.value)}
                    placeholder="Enter complete pickup address including city and state"
                    rows={3}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dropoffAddress" className="flex items-center space-x-2">
                    <MapPin className="h-4 w-4" />
                    <span>Drop-off Address *</span>
                  </Label>
                  <Textarea
                    id="dropoffAddress"
                    value={formData.dropoffAddress}
                    onChange={(e) => handleInputChange("dropoffAddress", e.target.value)}
                    placeholder="Enter complete drop-off address including city and state"
                    rows={3}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="tripDate" className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4" />
                    <span>Trip Date *</span>
                  </Label>
                  <Input
                    id="tripDate"
                    type="date"
                    value={formData.tripDate}
                    onChange={(e) => handleInputChange("tripDate", e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tripTime" className="flex items-center space-x-2">
                    <Clock className="h-4 w-4" />
                    <span>Preferred Time *</span>
                  </Label>
                  <Input
                    id="tripTime"
                    type="time"
                    value={formData.tripTime}
                    onChange={(e) => handleInputChange("tripTime", e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Transportation Type */}
              <div className="space-y-3">
                <Label className="flex items-center space-x-2">
                  <Accessibility className="h-4 w-4" />
                  <span>Transportation Needs *</span>
                </Label>
                <RadioGroup
                  value={formData.transportationType}
                  onValueChange={(value) => handleInputChange("transportationType", value)}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="wheelchair" id="wheelchair" />
                    <Label htmlFor="wheelchair">Wheelchair Accessible Vehicle</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="ambulatory" id="ambulatory" />
                    <Label htmlFor="ambulatory">Ambulatory (Walking) Transportation</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Payment Method */}
              <div className="space-y-3">
                <Label className="flex items-center space-x-2">
                  <CreditCard className="h-4 w-4" />
                  <span>Payment Method *</span>
                </Label>
                <RadioGroup
                  value={formData.paymentMethod}
                  onValueChange={(value) => handleInputChange("paymentMethod", value)}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="insurance" id="insurance" />
                    <Label htmlFor="insurance">Insurance Covered</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="self-pay" id="self-pay" />
                    <Label htmlFor="self-pay">Self-Pay</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Emergency Contacts Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 flex items-center">
                  <Users className="h-5 w-5 text-blue-600 mr-2" />
                  Emergency Contacts
                </h3>
                
                {/* Primary Emergency Contact */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Primary Emergency Contact *</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="emergencyContact1Name">Full Name *</Label>
                      <Input
                        id="emergencyContact1Name"
                        type="text"
                        value={formData.emergencyContact1Name}
                        onChange={(e) => handleInputChange("emergencyContact1Name", e.target.value)}
                        placeholder="Emergency contact name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="emergencyContact1Phone">Phone Number *</Label>
                      <Input
                        id="emergencyContact1Phone"
                        type="tel"
                        value={formData.emergencyContact1Phone}
                        onChange={(e) => handleInputChange("emergencyContact1Phone", e.target.value)}
                        placeholder="(269) 123-4567"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="emergencyContact1Relation">Relationship *</Label>
                      <Select value={formData.emergencyContact1Relation} onValueChange={(value) => handleInputChange("emergencyContact1Relation", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Relationship" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="spouse">Spouse</SelectItem>
                          <SelectItem value="child">Adult Child</SelectItem>
                          <SelectItem value="parent">Parent</SelectItem>
                          <SelectItem value="sibling">Sibling</SelectItem>
                          <SelectItem value="friend">Friend</SelectItem>
                          <SelectItem value="caregiver">Caregiver</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Secondary Emergency Contact */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Secondary Emergency Contact (Optional)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="emergencyContact2Name">Full Name</Label>
                      <Input
                        id="emergencyContact2Name"
                        type="text"
                        value={formData.emergencyContact2Name}
                        onChange={(e) => handleInputChange("emergencyContact2Name", e.target.value)}
                        placeholder="Second emergency contact name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="emergencyContact2Phone">Phone Number</Label>
                      <Input
                        id="emergencyContact2Phone"
                        type="tel"
                        value={formData.emergencyContact2Phone}
                        onChange={(e) => handleInputChange("emergencyContact2Phone", e.target.value)}
                        placeholder="(269) 123-4567"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="emergencyContact2Relation">Relationship</Label>
                      <Select value={formData.emergencyContact2Relation} onValueChange={(value) => handleInputChange("emergencyContact2Relation", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Relationship" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="spouse">Spouse</SelectItem>
                          <SelectItem value="child">Adult Child</SelectItem>
                          <SelectItem value="parent">Parent</SelectItem>
                          <SelectItem value="sibling">Sibling</SelectItem>
                          <SelectItem value="friend">Friend</SelectItem>
                          <SelectItem value="caregiver">Caregiver</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Medical & Mobility Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 flex items-center">
                  <Accessibility className="h-5 w-5 text-blue-600 mr-2" />
                  Medical & Mobility Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="mobilityAids">Mobility Aids Used</Label>
                    <Select value={formData.mobilityAids} onValueChange={(value) => handleInputChange("mobilityAids", value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select mobility aids" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="wheelchair">Wheelchair</SelectItem>
                        <SelectItem value="walker">Walker</SelectItem>
                        <SelectItem value="cane">Cane/Walking Stick</SelectItem>
                        <SelectItem value="crutches">Crutches</SelectItem>
                        <SelectItem value="scooter">Mobility Scooter</SelectItem>
                        <SelectItem value="multiple">Multiple aids</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="assistanceLevel">Assistance Level Needed</Label>
                    <Select value={formData.assistanceLevel} onValueChange={(value) => handleInputChange("assistanceLevel", value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select assistance level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="independent">Independent</SelectItem>
                        <SelectItem value="minimal">Minimal assistance</SelectItem>
                        <SelectItem value="moderate">Moderate assistance</SelectItem>
                        <SelectItem value="full">Full assistance required</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="medicalEquipment">Medical Equipment</Label>
                  <Textarea
                    id="medicalEquipment"
                    value={formData.medicalEquipment}
                    onChange={(e) => handleInputChange("medicalEquipment", e.target.value)}
                    placeholder="Oxygen tank, dialysis equipment, IV pole, etc."
                    rows={2}
                  />
                </div>
              </div>

              {/* Trip Preferences & Reminders */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 flex items-center">
                  <Clock className="h-5 w-5 text-blue-600 mr-2" />
                  Trip Preferences & Reminders
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="reminderPreference">Reminder Preference</Label>
                    <Select value={formData.reminderPreference} onValueChange={(value) => handleInputChange("reminderPreference", value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="How would you like to be reminded?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="phone">Phone call</SelectItem>
                        <SelectItem value="text">Text message (SMS)</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="both">Phone call and text</SelectItem>
                        <SelectItem value="none">No reminders needed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="returnTrip"
                        checked={formData.returnTrip}
                        onCheckedChange={(checked) => handleInputChange("returnTrip", checked)}
                      />
                      <Label htmlFor="returnTrip">I need return transportation</Label>
                    </div>
                    
                    {formData.returnTrip && (
                      <div className="mt-3">
                        <Label htmlFor="returnTime">Estimated Return Time</Label>
                        <Input
                          id="returnTime"
                          type="time"
                          value={formData.returnTime}
                          onChange={(e) => handleInputChange("returnTime", e.target.value)}
                          className="mt-1"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Insurance Information */}
              {formData.paymentMethod === "insurance" && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 flex items-center">
                    <CreditCard className="h-5 w-5 text-blue-600 mr-2" />
                    Insurance Information
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="insuranceCompany">Insurance Company</Label>
                      <Input
                        id="insuranceCompany"
                        type="text"
                        value={formData.insuranceCompany}
                        onChange={(e) => handleInputChange("insuranceCompany", e.target.value)}
                        placeholder="e.g., Medicare, Blue Cross, Priority Health"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="memberId">Member/Policy ID</Label>
                      <Input
                        id="memberId"
                        type="text"
                        value={formData.memberId}
                        onChange={(e) => handleInputChange("memberId", e.target.value)}
                        placeholder="Insurance member ID number"
                      />
                    </div>
                  </div>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        <p className="font-medium mb-1">Insurance Verification</p>
                        <p>We'll verify your coverage before your appointment. Some insurance plans require prior authorization for medical transportation.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Consent and Privacy */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 flex items-center">
                  <Shield className="h-5 w-5 text-blue-600 mr-2" />
                  Consent & Privacy
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="consentToContact"
                      checked={formData.consentToContact}
                      onCheckedChange={(checked) => handleInputChange("consentToContact", checked)}
                    />
                    <Label htmlFor="consentToContact" className="text-sm leading-relaxed">
                      I consent to be contacted by Quality Transport Services via phone, text, or email for appointment confirmations, reminders, and service-related communications. *
                    </Label>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="consentToShare"
                      checked={formData.consentToShare}
                      onCheckedChange={(checked) => handleInputChange("consentToShare", checked)}
                    />
                    <Label htmlFor="consentToShare" className="text-sm leading-relaxed">
                      I authorize Quality Transport Services to share necessary medical and transportation information with my healthcare providers and emergency contacts as needed for safe transportation services. This sharing is HIPAA-compliant and limited to what is necessary for your care. *
                    </Label>
                  </div>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">HIPAA Privacy Protection</p>
                      <p>Your health information is protected under HIPAA. We only share information as authorized by you or as required for your safe transportation. View our <a href="/privacy" className="underline">Privacy Policy</a> for complete details.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Special Requests */}
              <div className="space-y-2">
                <Label htmlFor="specialRequests">Special Requests or Additional Information</Label>
                <Textarea
                  id="specialRequests"
                  value={formData.specialRequests}
                  onChange={(e) => handleInputChange("specialRequests", e.target.value)}
                  placeholder="Any special accommodations, medical equipment, or additional information we should know"
                  rows={4}
                />
              </div>

              {/* Submit Button */}
              <div className="pt-6">
                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-blue-900 hover:bg-blue-800"
                  disabled={isSubmitting || !formData.consentToContact || !formData.consentToShare}
                >
                  {isSubmitting ? "Submitting Request..." : "Submit Transportation Request"}
                </Button>
                <p className="text-sm text-gray-500 mt-3 text-center">
                  By submitting this form, you agree to our <a href="/terms" className="underline">Terms of Service</a> and 
                  confirm that the information provided is accurate. We will confirm your booking within 24 hours.
                </p>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-2">Need immediate assistance or have questions?</p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-6">
            <div className="flex items-center space-x-2">
              <Phone className="h-4 w-4 text-blue-900" />
              <span className="font-semibold text-blue-900">(269) 248-3514</span>
            </div>
            <div className="flex items-center space-x-2">
              <Mail className="h-4 w-4 text-blue-900" />
              <span className="text-blue-900">qualitytransportservices42@gmail.com</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookRide;